import { Permissions, usePermissions } from '@app/src/auth/permissions'
import { useAccount } from '@app/src/context/AccountContext'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import PublicContactEnforcer from '@app/src/context/PublicContactEnforcer'
import useFeatureFlagsByAccessor from '@app/src/hooks/useFeatureFlagsByAccessor'
import { useScrollReveal } from '@app/src/hooks/useScrollReveal'
import { CONTENT_SCROLL_CONTAINER_ID, Solutions } from '@app/src/wf-constants'
import { Box } from '@mui/material'
import React from 'react'
import NavBreadcrumbs from '../Breadcrumbs/NavBreadcrumbs'
import AccountMenu from './AccountMenu'
import AiFileRepositoryLink from './AiFileRepositoryLink'
import AiJobsStatusIndicator from './AiJobsStatusIndicator'
import ConfigurationsMenuItem from './ConfigurationsMenuItem'
import NotificationsMenuItem from './NotificationsMenuItem'
import PlatformReleasesMenuItem from './PlatformReleasesMenuItem'
import SustainabilityLibraryMenuItem from './SustainabilityLibraryMenuItem'
import YourDataMenuItem from './YourDataMenuItem'

const HORIZONTAL_MENU_HEIGHT = 56

const HorizontalMenu = (): JSX.Element => {
  const { account } = useAccount()
  const { scope } = useAuthentication()
  const { hasPermission } = usePermissions()
  const { solution = '' } = scope
  const { orgOrAccessorHasFeature: suggestedAnswers } = useFeatureFlagsByAccessor({
    featureToggleName: 'suggested-answers',
    enabled: hasPermission(Permissions.TRANSPARENCY_USER),
    priorityValue: false,
  })
  const { isHidden } = useScrollReveal(CONTENT_SCROLL_CONTAINER_ID)

  const displayableSolutions = Object.values(Solutions)

  const solutionExists = solution && displayableSolutions.includes(String(solution))

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: theme => theme.zIndex.appBar,
        backgroundColor: 'grey.100',
        transform: isHidden ? 'translateY(-100%)' : 'translateY(0)',
        transition: 'transform 0.25s ease-in-out',
      }}
    >
      <PublicContactEnforcer />
      <Box
        sx={{
          height: HORIZONTAL_MENU_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 4,
          pl: 1,
        }}
      >
        {account?.organization ? <NavBreadcrumbs /> : <Box />}
        <Box
          sx={{
            height: HORIZONTAL_MENU_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {account && (
            <>
              <PlatformReleasesMenuItem />
              <AiJobsStatusIndicator />
              {solutionExists && solution !== Solutions.Transparency && <SustainabilityLibraryMenuItem />}
              {solutionExists && solution === Solutions.Transparency && <YourDataMenuItem />}
              {suggestedAnswers && hasPermission(Permissions.TRANSPARENCY_USER) && <AiFileRepositoryLink />}
              {hasPermission(Permissions.ACCESS_SOLUTION_CONFIGURATION) && <ConfigurationsMenuItem />}
              {solutionExists && <NotificationsMenuItem />}
              <AccountMenu account={account} />
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default HorizontalMenu
